import { activeCameraIdAtom } from "@/atoms/activeCamera";
import { activeRegionAtom } from "@/atoms/activeRegion";
import { homeSpotAtom } from "@/atoms/homeRegion";
import AuthPromptDialog from "@/components/Auth/AuthPromptDialog";
import Layout from "@/components/Layout";
import Player from "@/components/Player/Player";
import ConditionsAnswer from "@/components/Seo/ConditionsAnswer";
import FlaglerArticle from "@/components/Seo/FlaglerArticle";
import FaqSection from "@/components/Seo/FaqSection";
import HomeIntro from "@/components/Seo/HomeIntro";
import Seo from "@/components/Seo/Seo";
import SupportDialog from "@/components/SupportDialog";
import CompactForecast from "@/components/SurfDashboard/CompactForecast";
import ReportsSection from "@/components/SurfFeed/ReportsSection";
import ReportFormDialog from "@/components/SurfFeed/ReportFormDialog";
import { useHomeNavigation } from "@/hooks/useHomeNavigation";
import { useFlaglerReadings } from "@/hooks/useFlaglerReadings";
import { useSpotAnswer } from "@/hooks/useSpotAnswer";
import { writeLastViewedRegionId } from "@/lib/floridaHome";
import { usePageView } from "@/hooks/usePageView";
import WindMap from "@/components/WindMap/WindMap";
import {
  trackCameraChange,
  trackHomeSpotClear,
  trackHomeSpotSet,
  trackMultiCamOpen,
  trackRegionChange,
  trackShareCancel,
  trackShareClick,
  trackShareSuccess,
} from "@/lib/analytics";
import type { Camera } from "@/lib/regions";
import {
  useRegions,
  useZones,
  getCamerasFromRegion,
  findRegionInZones,
} from "@/hooks/useRegions";
import { useAuth } from "@/hooks/useAuth";
import { useRequireAuth } from "@/hooks/useRequireAuth";
import {
  buildFlaglerJsonLd,
  flaglerMetaDescription,
  flaglerPageFaqs,
  getFlaglerLanding,
} from "@/lib/flaglerLandings";
import type { SpotPageConfig } from "@/lib/spotPages";
import { buildSpotPageStructuredData, getRelatedSpotPages } from "@/lib/spotPages";
import { fontStack } from "@/theme";
import GridViewIcon from "@mui/icons-material/GridView";
import ShareIcon from "@mui/icons-material/Share";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import VideocamIcon from "@mui/icons-material/Videocam";
import {
  Box,
  Button,
  Chip,
  Container,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useRecoilState } from "recoil";

interface HomeProps {
  spotPage?: SpotPageConfig;
}

export default function Home({ spotPage }: HomeProps) {
  const [activeRegion, setActiveRegion] = useRecoilState(activeRegionAtom);
  const [savedCameraId, setSavedCameraId] = useRecoilState(activeCameraIdAtom);
  const [homeSpot, setHomeSpot] = useRecoilState(homeSpotAtom);
  const { regionId: routeRegionId, cameraId: routeCameraId } = useParams();
  const requestedRegionId = spotPage
    ? spotPage.regionId
    : routeRegionId
      ? decodeURIComponent(routeRegionId)
      : null;
  const requestedCameraId = spotPage
    ? spotPage.cameraId
    : routeCameraId
      ? decodeURIComponent(routeCameraId)
      : null;

  const { data: regionsData, isLoading: regionsLoading } = useRegions();
  const { data: zonesData, isLoading: zonesLoading } = useZones();

  const currentRegion =
    requestedRegionId && regionsData?.[requestedRegionId]
      ? requestedRegionId
      : activeRegion;
  const found = zonesData ? findRegionInZones(zonesData, currentRegion) : null;

  const navigate = useNavigate();

  const readyForPageView = useHomeNavigation({
    requestedRegionId,
    requestedCameraId,
    homeSpot,
    regionsData,
    isCanonicalSpotPage: Boolean(spotPage),
    activeRegionId: activeRegion,
  });
  usePageView(readyForPageView);

  // Apply URL params to state (URL → State, one direction only)
  const lastAppliedUrl = useRef("");
  useEffect(() => {
    if (!regionsData) return;

    const urlKey = `${requestedRegionId ?? ""}/${requestedCameraId ?? ""}`;
    if (urlKey === lastAppliedUrl.current) return;
    lastAppliedUrl.current = urlKey;

    if (requestedRegionId) {
      const region = regionsData[requestedRegionId];
      if (region) {
        writeLastViewedRegionId(requestedRegionId);
        const cameras = getCamerasFromRegion(region);
        const validCam =
          requestedCameraId && cameras.some((c) => c.id === requestedCameraId)
            ? requestedCameraId
            : null;
        setActiveRegion(requestedRegionId);
        setSavedCameraId(validCam);
      }
    }
  }, [
    requestedRegionId,
    requestedCameraId,
    regionsData,
    setActiveRegion,
    setSavedCameraId,
  ]);

  const { user } = useAuth();

  const [supportOpen, setSupportOpen] = useState(false);
  const cameraChangeCount = useRef(0);

  // Report form state
  const [formOpen, setFormOpen] = useState(false);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const [shareSnackOpen, setShareSnackOpen] = useState(false);

  // Cameras for the active region (Firestore-backed)
  const availableCameras = useMemo(() => {
    if (!regionsData) return [];
    const region = regionsData[currentRegion];
    return region ? getCamerasFromRegion(region) : [];
  }, [regionsData, currentRegion]);
  const hasCameras = availableCameras.length > 0;

  // Resolve active camera: try saved ID first, fall back to first camera
  const activeCamera = useMemo(() => {
    if (availableCameras.length === 0) return null;
    if (savedCameraId) {
      const saved = availableCameras.find((c) => c.id === savedCameraId);
      if (saved) return saved;
    }
    return availableCameras[0];
  }, [availableCameras, savedCameraId]);

  const handleReport = useCallback(() => {
    if (!user) {
      setAuthPromptOpen(true);
      return;
    }
    setFormOpen(true);
  }, [user]);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
  }, []);

  const handleSwitchCamera = useCallback(
    (camera: Camera) => {
      setSavedCameraId(camera.id);
      trackCameraChange(camera.name, currentRegion);
      navigate(
        `/spot/${encodeURIComponent(currentRegion)}/${encodeURIComponent(camera.id)}`,
        { replace: true },
      );
      cameraChangeCount.current += 1;
      if (cameraChangeCount.current % 3 === 0) {
        setSupportOpen(true);
      }
    },
    [setSavedCameraId, currentRegion, navigate],
  );

  const siblingRegions = found?.country.regions ?? [];
  const regionLabel = found?.region.label || currentRegion;

  const switchRegion = useCallback(
    (regionId: string) => {
      setActiveRegion(regionId);
      setSavedCameraId(null);
      trackRegionChange(regionId);
      navigate(`/spot/${encodeURIComponent(regionId)}`, { replace: true });
    },
    [setActiveRegion, setSavedCameraId, navigate],
  );

  const isHomeSpot =
    homeSpot?.regionId === currentRegion &&
    homeSpot?.cameraId === (activeCamera?.id ?? null);

  const { gate: gateHomeSpot, promptNode: homeSpotPrompt } = useRequireAuth({
    reason: "Sign in to save this break as your home spot.",
    source: "home_spot",
  });

  const toggleHomeSpot = useCallback(() => {
    if (isHomeSpot) {
      setHomeSpot(null);
      trackHomeSpotClear();
    } else if (activeCamera) {
      setHomeSpot({ regionId: currentRegion, cameraId: activeCamera.id });
      trackHomeSpotSet(currentRegion, activeCamera.id);
    } else {
      setHomeSpot({ regionId: currentRegion, cameraId: "" });
      trackHomeSpotSet(currentRegion, null);
    }
  }, [isHomeSpot, currentRegion, activeCamera, setHomeSpot]);

  const handleShare = useCallback(async () => {
    const cameraPath = activeCamera?.id ? `/${encodeURIComponent(activeCamera.id)}` : "";
    const sharePath =
      spotPage && activeCamera?.id === spotPage.cameraId
        ? spotPage.path
        : `/spot/${encodeURIComponent(currentRegion)}${cameraPath}`;
    const url = `${window.location.origin}${sharePath}`;

    trackShareClick();
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${regionLabel} — How Is The Surf`,
          url,
        });
        trackShareSuccess("native");
      } catch {
        // User dismissed the native share sheet. Some browsers throw
        // AbortError for dismissal and a DOMException for actual
        // failures; distinguishing adds little value, so log as cancel.
        trackShareCancel();
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        trackShareSuccess("clipboard");
      } catch {
        /* clipboard unavailable */
      }
      setShareSnackOpen(true);
    }
  }, [activeCamera, currentRegion, regionLabel, spotPage]);

  const relatedSpotPages = useMemo(
    () => (spotPage ? getRelatedSpotPages(spotPage) : []),
    [spotPage],
  );
  const flaglerLanding = spotPage ? getFlaglerLanding(spotPage.path) : null;
  const flaglerReadings = useFlaglerReadings(Boolean(flaglerLanding));
  const answer = useSpotAnswer(spotPage?.path ?? null);
  const flaglerFaqs = useMemo(
    () =>
      flaglerLanding
        ? flaglerPageFaqs(flaglerLanding, flaglerReadings, answer.faqs)
        : null,
    [answer.faqs, flaglerLanding, flaglerReadings],
  );
  const structuredData = useMemo(() => {
    if (flaglerLanding) return buildFlaglerJsonLd(flaglerLanding, flaglerReadings);
    return spotPage ? buildSpotPageStructuredData(spotPage) : undefined;
  }, [flaglerLanding, flaglerReadings, spotPage]);
  const seoSpotLabel = activeCamera?.name ?? spotPage?.displayName ?? regionLabel;
  const seoTitle =
    spotPage?.title ?? `${seoSpotLabel} Surf Report & Forecast | howisthe.surf`;
  const seoDescription = flaglerLanding
    ? flaglerMetaDescription(flaglerLanding, flaglerReadings)
    : (spotPage?.description ??
      `Check ${seoSpotLabel} surf conditions with wave forecast, tide, wind, buoy data, and community reports.`);
  const requestedCameraIsValid = Boolean(
    requestedCameraId &&
    availableCameras.some((camera) => camera.id === requestedCameraId),
  );
  const waitingForRequestedRoute = Boolean(
    requestedRegionId &&
    regionsData?.[requestedRegionId] &&
    (activeRegion !== requestedRegionId ||
      (requestedCameraIsValid && savedCameraId !== requestedCameraId)),
  );
  const pageIntro = spotPage ? (
    <Box
      component="section"
      aria-labelledby="spot-page-heading"
      sx={{ mb: { xs: 2, md: 2.5 }, maxWidth: 820 }}
    >
      <Typography
        id="spot-page-heading"
        component="h1"
        sx={{
          fontFamily: fontStack.serif,
          fontSize: { xs: "1.45rem", md: "1.85rem" },
          letterSpacing: 0,
          lineHeight: 1.15,
          color: "ink.main",
          mb: 1,
        }}
      >
        {spotPage.introTitle}
      </Typography>
      {answer.sentence ? <ConditionsAnswer text={answer.sentence} /> : null}
      <Typography
        sx={{
          maxWidth: 780,
          color: "text.secondary",
          fontSize: { xs: "0.95rem", md: "1rem" },
          lineHeight: 1.7,
        }}
      >
        {spotPage.introBody}
      </Typography>
      {flaglerLanding ? (
        <FlaglerArticle
          page={flaglerLanding}
          readings={flaglerReadings}
          faqs={flaglerFaqs ?? []}
          showIntro={false}
          showAbout
        />
      ) : null}
      {relatedSpotPages.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            mt: 1.75,
          }}
        >
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "0.78rem",
              textTransform: "uppercase",
              letterSpacing: 0,
            }}
          >
            Nearby reports
          </Typography>
          {relatedSpotPages.map((page) => (
            <Button
              key={page.path}
              component={RouterLink}
              to={page.path}
              size="small"
              variant="outlined"
              sx={{
                color: "ink.main",
                borderColor: "rule.strong",
                fontSize: "0.8125rem",
                "&:hover": {
                  borderColor: "ink.main",
                  backgroundColor: "transparent",
                },
              }}
            >
              {page.spotName}
            </Button>
          ))}
        </Box>
      )}
      {!flaglerLanding && answer.faqs ? <FaqSection items={answer.faqs} /> : null}
    </Box>
  ) : !requestedRegionId ? (
    <HomeIntro />
  ) : null;

  // Wait for Firestore data before rendering
  if (regionsLoading || zonesLoading || waitingForRequestedRoute) {
    return (
      <>
        <Seo
          title={seoTitle}
          description={seoDescription}
          canonicalPath={spotPage?.path}
          structuredData={structuredData}
        />
        <Layout>
          <Container maxWidth="xl" sx={{ py: { xs: 1.5, md: 2 } }}>
            {pageIntro}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <Typography sx={{ color: "text.secondary" }}>Loading...</Typography>
            </Box>
          </Container>
        </Layout>
      </>
    );
  }

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonicalPath={spotPage?.path}
        structuredData={structuredData}
      />
      <Layout>
        <Container maxWidth="xl" sx={{ py: { xs: 1.5, md: 2 } }}>
          {pageIntro}
          {/* Region selector — dropdown on mobile (keeps the hero above the
            fold), chips on sm+ where scannability matters. */}
          {siblingRegions.length > 1 && (
            <>
              <Box sx={{ display: { xs: "block", sm: "none" }, mb: 1.5 }}>
                <Select
                  fullWidth
                  size="small"
                  value={currentRegion}
                  onChange={(e) => switchRegion(e.target.value)}
                  sx={{ fontSize: "0.875rem" }}
                >
                  {siblingRegions.map((region) => (
                    <MenuItem
                      key={region.id}
                      value={region.id}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      {region.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: 0.75,
                  mb: 2,
                }}
              >
                {siblingRegions.map((region) => {
                  const isActive = currentRegion === region.id;
                  return (
                    <Chip
                      key={region.id}
                      label={region.label}
                      size="small"
                      onClick={() => switchRegion(region.id)}
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: isActive ? 600 : 400,
                        backgroundColor: "transparent",
                        color: isActive ? "accent.main" : "text.secondary",
                        border: "1px solid",
                        borderColor: isActive ? "accent.main" : "rule.main",
                        "&:hover": {
                          backgroundColor: "transparent",
                          borderColor: isActive ? "accent.deep" : "rule.strong",
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </>
          )}

          {/* Camera player — hero content */}
          {activeCamera && (
            <Player
              index={0}
              name={activeCamera.name}
              url={activeCamera.url}
              camera={activeCamera}
              availableCameras={availableCameras}
              canDelete={false}
              onCameraChange={handleSwitchCamera}
              onDelete={() => {}}
            />
          )}

          {/* Action buttons — DESIGN.md secondary pattern: 1px ink border,
            ink text, transparent fill. Home Spot is the only one that gets
            the accent treatment when active, keeping the "rule of one
            accent per screen" together with the chart's NOW marker.
            On mobile we collapse to icon-only so all four fit on one row. */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: { xs: 0.5, sm: 1 },
              mt: { xs: 1, sm: 2 },
              mb: { xs: 0.5, sm: 1 },
              flexWrap: { xs: "nowrap", sm: "wrap" },
            }}
          >
            <Button
              size="small"
              variant="outlined"
              aria-label={isHomeSpot ? "Home Spot" : "Set Home"}
              startIcon={
                isHomeSpot ? (
                  <StarIcon sx={{ fontSize: 16 }} />
                ) : (
                  <StarBorderIcon sx={{ fontSize: 16 }} />
                )
              }
              onClick={gateHomeSpot(toggleHomeSpot)}
              sx={{
                color: isHomeSpot ? "accent.main" : "ink.main",
                borderColor: isHomeSpot ? "accent.main" : "rule.strong",
                fontSize: "0.8125rem",
                minWidth: { xs: 0, sm: "auto" },
                px: { xs: 1, sm: 2 },
                "& .MuiButton-startIcon": { mr: { xs: 0, sm: "6px" } },
                "&:hover": {
                  borderColor: isHomeSpot ? "accent.deep" : "ink.main",
                  color: isHomeSpot ? "accent.deep" : "ink.main",
                  backgroundColor: "transparent",
                },
              }}
            >
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                {isHomeSpot ? "Home Spot" : "Set Home"}
              </Box>
            </Button>
            {availableCameras.length > 1 && hasCameras && (
              <Button
                size="small"
                variant="outlined"
                aria-label={`Multi-Cam (${availableCameras.length})`}
                startIcon={<GridViewIcon sx={{ fontSize: 16 }} />}
                onClick={() => {
                  trackMultiCamOpen(availableCameras.length);
                  navigate(`/multicam/${encodeURIComponent(currentRegion)}`);
                }}
                sx={{
                  color: "ink.main",
                  borderColor: "rule.strong",
                  fontSize: "0.8125rem",
                  minWidth: { xs: 0, sm: "auto" },
                  px: { xs: 1, sm: 2 },
                  "& .MuiButton-startIcon": { mr: { xs: 0, sm: "6px" } },
                  "&:hover": {
                    borderColor: "ink.main",
                    backgroundColor: "transparent",
                  },
                }}
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Multi-Cam ({availableCameras.length})
                </Box>
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              aria-label="Suggest Cam"
              startIcon={<VideocamIcon sx={{ fontSize: 16 }} />}
              href={`mailto:hello@howisthe.surf?subject=Camera suggestion — ${regionLabel}&body=I'd like to suggest a camera for ${regionLabel}.`}
              sx={{
                color: "ink.main",
                borderColor: "rule.strong",
                fontSize: "0.8125rem",
                minWidth: { xs: 0, sm: "auto" },
                px: { xs: 1, sm: 2 },
                "& .MuiButton-startIcon": { mr: { xs: 0, sm: "6px" } },
                "&:hover": {
                  borderColor: "ink.main",
                  backgroundColor: "transparent",
                },
              }}
            >
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                Suggest Cam
              </Box>
            </Button>
            <Button
              size="small"
              variant="outlined"
              aria-label="Share"
              startIcon={<ShareIcon sx={{ fontSize: 16 }} />}
              onClick={handleShare}
              sx={{
                color: "ink.main",
                borderColor: "rule.strong",
                fontSize: "0.8125rem",
                minWidth: { xs: 0, sm: "auto" },
                px: { xs: 1, sm: 2 },
                "& .MuiButton-startIcon": { mr: { xs: 0, sm: "6px" } },
                "&:hover": {
                  borderColor: "ink.main",
                  backgroundColor: "transparent",
                },
              }}
            >
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                Share
              </Box>
            </Button>
          </Box>

          {/* Conditions readout */}
          <CompactForecast />

          {/* Community reports — pulse strip + individual reports.
            When there's no webcam for this region, the section becomes
            the primary interaction surface via an expanded empty-state. */}
          {/* Reporting becomes the primary surface when there's no streamable
            video — either no camera for the region, or the active camera is
            a link-only reference (no embeddable stream URL). */}
          <ReportsSection
            onReport={handleReport}
            onAuthRequired={() => setAuthPromptOpen(true)}
            prominent={!activeCamera || !activeCamera.url || activeCamera.kind === "link"}
            cameraName={activeCamera?.name ?? regionLabel}
          />

          {/* Wind map */}
          <WindMap />

          {/* Report form — always for the active camera */}
          <ReportFormDialog
            open={formOpen}
            onClose={handleFormClose}
            regionId={currentRegion}
            regionLabel={regionLabel}
            camera={activeCamera}
          />
          <AuthPromptDialog
            open={authPromptOpen}
            onClose={() => setAuthPromptOpen(false)}
            source="report_attempt"
            reason="Sign in to post surf reports and vote on conditions."
          />
          {homeSpotPrompt}

          <SupportDialog
            open={supportOpen}
            source="camera_switch_prompt"
            onClose={() => setSupportOpen(false)}
          />
        </Container>
        <Snackbar
          open={shareSnackOpen}
          autoHideDuration={2000}
          onClose={() => setShareSnackOpen(false)}
          message="Link copied to clipboard"
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
      </Layout>
    </>
  );
}
